/**
 * Origin-offset utility for the Three.js + WebGPU renderer.
 *
 * # Why this module exists
 *
 * DXF surveys, geodetic site plans and large-scale infrastructure
 * drawings frequently use absolute coordinates in the 1e7..1e9 range
 * (UTM eastings, state-plane northings). Three.js stores positions in
 * Float32 buffers — Float32 has ~7 decimal digits of precision, so a
 * coordinate of 6_543_210.789 lands on the GPU as 6_543_210.5, which
 * jitters under sub-pixel zoom and creates visible Z-fighting on long
 * lines that span the bbox.
 *
 * The fix (matching what AutoCAD does internally with WCS/ECS) is to
 * subtract a one-time origin from every coordinate before it reaches
 * the GPU buffer, then add it back on coords-bar display and on DXF
 * export. The renderer never sees a number larger than ±1e6 from
 * origin → 32-bit precision is plenty.
 *
 * # Lock semantics (Q27.A keep-first)
 *
 * The first DXF imported in a session locks the origin. Subsequent
 * imports re-use the same offset — even if their bbox is far away —
 * so coords-bar readouts and round-tripped DXFs stay consistent. The
 * setter throws on second call to make the contract loud; the only
 * way to reset is `__resetCoordinateOriginForTest()`, which is also
 * the only way unit tests can stage independent fixtures.
 */

import type { Vec3 } from "./types";

let _coordinateOrigin: Vec3 | null = null;

/**
 * Returns the locked-in origin, or `null` if no DXF has been imported
 * yet. Adapters MUST guard against `null` and treat it as identity
 * (no offset) — this matches the behaviour expected by jsdom unit
 * tests that exercise the adapter without first calling
 * `setCoordinateOrigin`.
 */
export function getCoordinateOrigin(): Vec3 | null {
  return _coordinateOrigin;
}

/**
 * Locks in the origin for the lifetime of the session. Throws on the
 * second call (Q27.A keep-first decision) — callers are expected to
 * read `getCoordinateOrigin()` first and skip this call when an
 * origin is already set.
 *
 * Vec3 is shallow-copied so subsequent mutation of the input does not
 * leak into stored state.
 */
export function setCoordinateOrigin(origin: Vec3): void {
  if (_coordinateOrigin !== null) {
    throw new Error(
      "setCoordinateOrigin: origin already set; use __resetCoordinateOriginForTest in tests",
    );
  }
  _coordinateOrigin = { x: origin.x, y: origin.y, z: origin.z };
}

/**
 * Returns `v − origin` (offset-world coordinate). When no origin is
 * set, returns a fresh copy of `v` unchanged. The new object is
 * always a fresh allocation so adapters can mutate the result without
 * worrying about aliasing the input.
 */
export function applyOffset(v: Vec3): Vec3 {
  if (_coordinateOrigin === null) return { x: v.x, y: v.y, z: v.z };
  return {
    x: v.x - _coordinateOrigin.x,
    y: v.y - _coordinateOrigin.y,
    z: v.z - _coordinateOrigin.z,
  };
}

/**
 * Inverse of `applyOffset`: returns `v + origin` (AutoCAD-world
 * coordinate). Used by the coords-bar readout and the DXF export
 * adapter so users always see / save original coordinates.
 */
export function unapplyOffset(v: Vec3): Vec3 {
  if (_coordinateOrigin === null) return { x: v.x, y: v.y, z: v.z };
  return {
    x: v.x + _coordinateOrigin.x,
    y: v.y + _coordinateOrigin.y,
    z: v.z + _coordinateOrigin.z,
  };
}

/**
 * Test-only reset hook. NOT part of the public barrel; tests import
 * this symbol directly. Production code calling this is a bug — the
 * keep-first lock invariant is what makes coordinate handling
 * deterministic across imports.
 */
export function __resetCoordinateOriginForTest(): void {
  _coordinateOrigin = null;
}

/**
 * Axis-aligned 2D box, structurally the `RenderableEntity["bounds"]` /
 * `EntityMetadata["bounds"]` shape. Declared locally so this leaf module
 * keeps depending on nothing but `Vec3`.
 */
export interface OffsetBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Box form of {@link applyOffset}: translates a RAW/AutoCAD-world bbox into
 * the renderer's OFFSET-world frame. The origin is a pure translation, so
 * mapping the two corners maps the whole box exactly and cannot flip
 * `min`/`max`.
 *
 * This is the ONE truth for "put a document-frame box into the render frame",
 * shared by the cache-direct spatial-stub builder
 * (`state/spatialBuilder.ts buildSpatialRecordsForSpaces`), the paper-space
 * viewport cull query (`viewports/viewportCullingAdapter.ts`) and the test
 * bridge's raw-world window-select probe (`testBridge.ts
 * windowSelectWorldIds`), so they can never drift into different conversions
 * of the same quantity.
 *
 * No origin locked → identity (a fresh copy), same as `applyOffset`.
 */
export function applyOffsetToBounds(bounds: OffsetBounds): OffsetBounds {
  const min = applyOffset({ x: bounds.minX, y: bounds.minY, z: 0 });
  const max = applyOffset({ x: bounds.maxX, y: bounds.maxY, z: 0 });
  return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
}

/**
 * 2D form of a paper-space sheet origin (`LayoutPageSetup.origin`, DXF group
 * 10/20 `minimumLimits`). Declared locally, like {@link OffsetBounds}, so this
 * leaf module keeps depending on nothing but `Vec3`.
 */
export interface PaperOriginLike {
  x: number;
  y: number;
}

/**
 * Planar form of {@link applyOffset}: DOCUMENT (AutoCAD/DXF) point → the
 * renderer's OFFSET-world frame. The origin is a pure translation, so the z
 * axis is irrelevant to the planar pointer/draft pipeline and dropping it
 * keeps call sites from minting throwaway `z: 0` literals.
 *
 * This is the 2D half of the ONE frame conversion pair the pointer/draft
 * pipeline uses: `ActiveInputSpace.toDocument` / `.fromDocument`
 * (`viewports/activeInputSpace.ts`) are built on it, the in-VP draft-preview
 * mapping (`draft/draftPreviewMapping.ts`) uses it to undo the adapter's own
 * offset before the paper affine, and the DI overlay projects with it.
 *
 * No origin locked → identity (a fresh copy), same as {@link applyOffset}.
 */
export function applyOffsetToPoint2d(p: PaperOriginLike): PaperOriginLike {
  const v = applyOffset({ x: p.x, y: p.y, z: 0 });
  return { x: v.x, y: v.y };
}

/**
 * Planar form of {@link unapplyOffset}: OFFSET-world point → the DOCUMENT
 * (AutoCAD/DXF) frame. Exact inverse of {@link applyOffsetToPoint2d}.
 *
 * This is the conversion that makes a pointer coordinate safe to persist:
 * `clientToWorld` unprojects against the OFFSET-world camera, so every raw
 * pointer coordinate in the app is offset-world, and anything written into a
 * `DrawingEntity` must go through here first (the grip-drag commit does the
 * same via {@link unapplyOffset} — see `scene/StageGripDrag.ts`).
 */
export function unapplyOffsetToPoint2d(p: PaperOriginLike): PaperOriginLike {
  const v = unapplyOffset({ x: p.x, y: p.y, z: 0 });
  return { x: v.x, y: v.y };
}

/**
 * The ONE conversion of a layout's `LayoutPageSetup.origin` from the DOCUMENT
 * frame (what the DXF stores, what AutoCAD shows) into the renderer's
 * OFFSET-world frame.
 *
 * # Why this exists
 *
 * Every geometry adapter runs {@link applyOffset} unconditionally, with no
 * per-space distinction — a paper-space LINE, TEXT or title block is put on
 * the GPU at `v − O` exactly like a model-space one. The paper SHEET and the
 * layout camera used to be placed from the RAW `pageSetup.origin`, so on any
 * drawing whose locked origin is non-zero the sheet sat a whole origin away
 * from the entities drawn on it: the layout rendered a blank white page while
 * its content was a million units off-screen (measured: layout «ФП1», origin
 * (1e6, 1e6) → 367710 white pixels, 0 ink).
 *
 * The renderer has ONE frame. Paper space joins it here: every consumer of the
 * sheet origin — the sheet mesh, the layout auto-fit camera, the VP frame
 * meshes, the scissor rects, the paper↔model viewport transform and the
 * pointer hit-tests — takes the value this function returns, never
 * `pageSetup.origin` directly. A per-space frame rule would be the second
 * source of truth CLAUDE.md PRIORITY #2.1 forbids.
 *
 * # What stays in the document frame
 *
 * The DRAFT STATE MACHINE. It works in DOCUMENT coordinates (so that what it
 * commits is what AutoCAD shows), so it resolves the sheet origin through its
 * OWN derivation — `hooks/useDraftSession.resolveDraftPaperOriginDocument`,
 * which deliberately does NOT call this function. Two paper-origin values
 * therefore exist, one per frame, and each derivation site names the other:
 * this one (via `Scene3DStage.paperOriginOffset`) serves everything that
 * hit-tests or renders against the camera; the document one serves
 * `ctx.paperOrigin` inside the draft machine. Handing either frame's value to
 * the other's consumer misses by the whole session origin.
 *
 * `ViewportEntity.center` is SHEET-LOCAL (relative to the sheet origin) and
 * `ViewportEntity.viewCenter` is a document-frame MODEL coordinate — neither
 * is touched. (Orthogonally to THIS origin question, `viewCenter` is in DCS,
 * not WCS: the WCS point at the centre of the window is `viewTarget +
 * R(viewTwistAngle)·viewCenter`, `drawing-model`'s `viewportWcsViewCenter`.
 * That is a different
 * axis and no concern of this function, which is purely about the session
 * origin.) `viewportTransform2d`'s `paperToModel` / `modelToPaper` /
 * `pointInViewport` only ever use DIFFERENCES of paper quantities
 * (`paperPt − (vp.center + paperOrigin)`), which a pure translation leaves
 * invariant, so they need no arithmetic change — provided BOTH sides are in
 * the same frame, which is what routing every consumer through this function
 * finally guarantees.
 *
 * No origin locked → identity (a fresh copy), same as {@link applyOffset}.
 */
export function applyOffsetToPaperOrigin(
  origin: PaperOriginLike,
): PaperOriginLike {
  return applyOffsetToPoint2d(origin);
}
